import { durationMs } from "odyssey-shared";
import { say } from "../../words.ts";

/**
 * The discovery notice (P2.11-T8, ADR-096 decision 15; the design's `Discovery` toast in the toast column, its `area`
 * state's tone and icon): a shrine the page's own character has just discovered raises a toast naming it, with the XP
 * the server granted for it as a third line when it granted any (P2.11-T16), a System
 * line in chat and the discovery stinger. The server tells one discovery a `shrines` message, so two shrines
 * discovered in one tick arrive a tick apart and stand in the column together, the newer below. A toast enters on the
 * motion table's `toast-in`, shows for six seconds, and is marked leaving so its exit, a single blink of the table's
 * `blink`, can play before it is gone; under reduced motion there is no travel and no blink and it stands for the same
 * time.
 */

/**
 * How long a discovery toast shows before it leaves, in ms: the design's 6 s, longer than a gain's, since it carries
 * a line to read.
 */
export const DISCOVERY_TOAST_MS = 6000;

/**
 * The most discovery toasts the column holds at once; a discovery past it replaces the oldest.
 */
export const MAX_DISCOVERY_TOASTS = 2;

/**
 * The toast's title, the author's wording (ADR-096's amendment of 2026-10-05).
 *
 * @returns The title.
 */
export function discoveryTitle(): string {
  return say("hud.discovery.title");
}

/**
 * The sentence of a discovery, the toast's text and the chat System line alike: "You have discovered X.", X the shrine's
 * name, or "a shrine" for one with none. The wording is the author's (ADR-096's amendment of 2026-10-05): it says nothing of where
 * a character returns to, which is not always that shrine.
 *
 * @param name The shrine's name as `shrines` carries it, empty for none.
 * @returns The sentence.
 * @example
 * discoveryText("Old Shrine");
 * // "You have discovered Old Shrine."
 * discoveryText("");
 * // "You have discovered a shrine."
 */
export function discoveryText(name: string): string {
  return say("hud.discovery.text", { name: name === "" ? say("hud.discovery.aShrine") : name });
}

/**
 * The toast's third line: the XP the server granted for the discovery, as the design's gain toast writes an amount, with
 * no word for its source (the author's wording, ADR-096's amendment of 2026-10-05). The page never computes the
 * number: it is the one `shrines` carried.
 *
 * @param xp The XP granted, 0 for none.
 * @returns The line, or null when nothing was granted.
 * @example
 * discoveryGain(50);
 * // "+50 XP"
 */
export function discoveryGain(xp: number): string | null {
  return xp > 0 ? say("hud.discovery.gain", { xp: String(xp) }) : null;
}

/**
 * One discovery toast as the column draws it: a key that is its own for the life of the page, its words, the gain line
 * when the server granted XP, and whether it is leaving.
 */
export interface DiscoveryShown {
  readonly id: number;
  readonly title: string;
  readonly text: string;
  readonly gain: string | null;
  readonly leaving: boolean;
}

/**
 * A timer: runs a call after a wait and returns its cancel.
 */
export type DiscoverySchedule = (fn: () => void, ms: number) => () => void;

/**
 * Where a discovery is told besides the toast: the chat log's plain line and the stinger only the player hears.
 */
export interface DiscoveryOutlets {
  readonly toast?: { readonly show: (name: string, xp: number) => void } | undefined;
  readonly chat?: { readonly note: (text: string) => void } | undefined;
  readonly sounds?: { readonly discovered: () => void } | undefined;
}

/**
 * Tells the player of a discovery: the toast, with the XP granted, the chat line and the stinger, once each.
 *
 * @param name The shrine's name, empty for none.
 * @param xp The XP the server granted for it, 0 for none.
 * @param outlets Where it is told; an outlet the page lacks is skipped.
 */
export function announceDiscovery(name: string, xp: number, outlets: DiscoveryOutlets): void {
  outlets.toast?.show(name, xp);
  outlets.chat?.note(discoveryText(name));
  outlets.sounds?.discovered();
}

/**
 * The discovery toasts' state and timers, which the HUD's toast column reads through `subscribe` and `getSnapshot`.
 *
 * @example
 * ```ts
 * const toasts = new DiscoveryToasts((fn, ms) => { const t = setTimeout(fn, ms); return () => clearTimeout(t); });
 * toasts.show("Old Shrine", 50);
 * toasts.getSnapshot();
 * // [{ id: 1, title: "Shrine Discovered", text: "You have discovered Old Shrine.", gain: "+50 XP", leaving: false }]
 * ```
 */
export class DiscoveryToasts {
  private shown: readonly DiscoveryShown[] = [];
  private readonly listeners = new Set<() => void>();
  private readonly cancels = new Map<number, (() => void)[]>();
  private next = 1;

  /**
   * @param schedule The timer the toasts' time runs on.
   */
  public constructor(private readonly schedule: DiscoverySchedule) {}

  /**
   * Adds a listener for changed toasts.
   *
   * @param listener Called after each change.
   * @returns A function that removes it.
   */
  public readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  /**
   * The toasts showing now, oldest first; the same list until one changes.
   *
   * @returns The toasts.
   */
  public readonly getSnapshot = (): readonly DiscoveryShown[] => this.shown;

  /**
   * Shows a toast for a discovered shrine under the toasts already showing, and starts its time. Past the column's
   * room the oldest toast goes at once.
   *
   * @param name The shrine's name, empty for none.
   * @param xp The XP the server granted for the discovery, 0 for none, which leaves the third line out.
   */
  public show(name: string, xp = 0): void {
    const id = this.next;
    this.next += 1;
    const kept = this.shown.length >= MAX_DISCOVERY_TOASTS ? this.shown.slice(this.shown.length - MAX_DISCOVERY_TOASTS + 1) : this.shown;
    for (const toast of this.shown) {
      if (!kept.includes(toast)) {
        this.stop(toast.id);
      }
    }
    this.set([...kept, { id, title: discoveryTitle(), text: discoveryText(name), gain: discoveryGain(xp), leaving: false }]);
    this.cancels.set(id, [
      this.schedule(() => this.set(this.shown.map((toast) => (toast.id === id ? { ...toast, leaving: true } : toast))), DISCOVERY_TOAST_MS),
      this.schedule(() => {
        this.cancels.delete(id);
        this.set(this.shown.filter((toast) => toast.id !== id));
      }, DISCOVERY_TOAST_MS + durationMs("blink")),
    ]);
  }

  /**
   * Removes every toast at once and stops their time, as a session's end must.
   */
  public clear(): void {
    for (const id of [...this.cancels.keys()]) {
      this.stop(id);
    }
    if (this.shown.length > 0) {
      this.set([]);
    }
  }

  /**
   * Cancels one toast's running timers.
   *
   * @param id The toast's id.
   */
  private stop(id: number): void {
    for (const cancel of this.cancels.get(id) ?? []) {
      cancel();
    }
    this.cancels.delete(id);
  }

  /**
   * Replaces the toasts showing and tells the listeners.
   *
   * @param shown The new list.
   */
  private set(shown: readonly DiscoveryShown[]): void {
    this.shown = shown;
    for (const listener of this.listeners) {
      listener();
    }
  }
}
